/**
 * Pure EffectiveSLP domain helpers.
 *
 * Anything that does not import Supabase lives here so it can be
 * unit-tested under tsx without mocking auth or env vars. This
 * module is the single source of truth for:
 *
 *  - The 8 disorder categories the picker exposes (PRD §12 #5)
 *  - The 7 goal areas (PRD §5 + §3 #2)
 *  - The 5 goal statuses
 *  - The 5 cueing levels (PRD §3 #5)
 *  - The 5 session statuses
 *  - The display labels for each enum
 *  - A pure helper to format disorder-category chips
 *
 * Pure functions only. NO Supabase imports in this file.
 */

export const DISORDER_CATEGORIES = [
  'apraxia',
  'articulation',
  'phonology',
  'fluency',
  'receptive_language',
  'expressive_language',
  'aac',
  'social_pragmatic',
] as const;

export type DisorderCategory = (typeof DISORDER_CATEGORIES)[number];

export const GOAL_AREAS = [
  'articulation',
  'receptive_language',
  'expressive_language',
  'fluency',
  'social',
  'aac',
  'voice',
  'cognitive',
] as const;

export type GoalArea = (typeof GOAL_AREAS)[number];

export const GOAL_STATUSES = ['active', 'mastered', 'discontinued'] as const;
export type GoalStatus = (typeof GOAL_STATUSES)[number];

export const CUEING_LEVELS = [
  'independent',
  'verbal',
  'visual',
  'tactile',
  'physical',
] as const;
export type CueingLevel = (typeof CUEING_LEVELS)[number];

export const SESSION_STATUSES = ['scheduled', 'completed', 'cancelled'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const PROFESSIONS = [
  'school',
  'private_practice',
  'early_intervention',
  'hospital',
  'parent',
] as const;
export type Profession = (typeof PROFESSIONS)[number];

/**
 * Display label for a disorder category. Pure function — the
 * `caseTransform` helper centralizes the snake-case → "Snake Case"
 * rendering so every screen renders the chip identically.
 */
export function labelForDisorderCategory(value: string): string {
  switch (value) {
    case 'apraxia':
      return 'Apraxia';
    case 'articulation':
      return 'Articulation';
    case 'phonology':
      return 'Phonology';
    case 'fluency':
      return 'Fluency';
    case 'receptive_language':
      return 'Receptive Language';
    case 'expressive_language':
      return 'Expressive Language';
    case 'aac':
      return 'AAC';
    case 'social_pragmatic':
      return 'Social-Pragmatic';
    default:
      return caseTransform(value);
  }
}

/**
 * Display label for a goal area. Pure function.
 */
export function labelForGoalArea(value: string): string {
  switch (value) {
    case 'articulation':
      return 'Articulation';
    case 'receptive_language':
      return 'Receptive language';
    case 'expressive_language':
      return 'Expressive language';
    case 'fluency':
      return 'Fluency';
    case 'social':
      return 'Social';
    case 'aac':
      return 'AAC';
    case 'voice':
      return 'Voice';
    case 'cognitive':
      return 'Cognitive';
    default:
      return caseTransform(value);
  }
}

export function labelForGoalStatus(value: string): string {
  switch (value) {
    case 'active':
      return 'Active';
    case 'mastered':
      return 'Mastered';
    case 'discontinued':
      return 'Discontinued';
    default:
      return caseTransform(value);
  }
}

export function labelForCueingLevel(value: string): string {
  switch (value) {
    case 'independent':
      return 'Independent';
    case 'verbal':
      return 'Verbal';
    case 'visual':
      return 'Visual';
    case 'tactile':
      return 'Tactile';
    case 'physical':
      return 'Physical';
    default:
      return caseTransform(value);
  }
}

export function labelForSessionStatus(value: string): string {
  switch (value) {
    case 'scheduled':
      return 'Scheduled';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return caseTransform(value);
  }
}

/**
 * Generic snake_case → Title Case. Used as the fallback for any
 * enum value we haven't enumerated explicitly. Visible for unit
 * tests.
 */
export function caseTransform(value: string): string {
  return value
    .split('_')
    .map((part) => (part.length === 0 ? '' : part[0]?.toUpperCase() + part.slice(1)))
    .join(' ');
}

/**
 * Renders an ISO date (timestamptz UTC, Postgres-canonical) as a
 * short date in the user's locale, e.g. "Jun 30, 2026". Pure
 * function — no Intl-injected side effects. Used to render the
 * "last session" chip on the caseload row.
 */
export function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Minimal session shape used by the next/last-session pickers
 * below. Defined structurally so the helper accepts either the
 * full SessionRow or the lighter SessionListRow used by the
 * student detail screen — both expose `scheduled_at` and
 * `completed_at`.
 *
 * Treated as `unknown`-narrow via `in` checks to stay loose for
 * test fixtures without forcing the caller to duplicate a complex
 * Supabase row type. See StudentDetailScreen (#138).
 */
export interface SchedulableSession {
  scheduled_at: string;
  completed_at: string | null;
}

/**
 * The "Next session" shortcut on StudentDetailScreen used to
 * index `sessions[0]` (most-recent-by-scheduled_at-desc) and
 * label it "Next" whenever `completed_at == null`. With a missed
 * past session + a future session, the past was labelled "Next"
 * (#138). This helper picks the row correctly:
 *
 *   `completed_at == null && scheduled_at >= now`
 *   → return the one with the EARLIEST scheduled_at
 *     (the soonest upcoming).
 *
 * `now` is an injected epoch-ms so the helper is pure /
 * deterministic / unit-testable. Returns `undefined` when no
 * upcoming not-yet-completed session exists.
 */
export function pickNextSession<T extends SchedulableSession>(
  sessions: readonly T[],
  now: number,
): T | undefined {
  let best: T | undefined;
  let bestTs = Infinity;
  for (const s of sessions) {
    if (s.completed_at != null) continue;
    const ts = Date.parse(s.scheduled_at);
    if (Number.isNaN(ts)) continue;
    if (ts < now) continue;
    if (ts < bestTs) {
      best = s;
      bestTs = ts;
    }
  }
  return best;
}

/**
 * Companion to `pickNextSession`. Picks the most recent session
 * whose `scheduled_at` is strictly BEFORE `now` — i.e. the
 * session "Last session" should refer to. The previous "Last"
 * label was driven solely by `completed_at != null`, which let
 * a future pre-completed session masquerade as "Last" and —
 * worse — let a missed past session masquerade as "Next"
 * (#138).
 *
 * Most-recent past subsumes "most-recently-completed past",
 * which is the spirit of the original "Last session" affordance.
 * Returns `undefined` when the student has no past sessions at
 * all (e.g. every session is still upcoming, or there are none).
 */
export function pickLastSession<T extends SchedulableSession>(
  sessions: readonly T[],
  now: number,
): T | undefined {
  let best: T | undefined;
  let bestTs = -Infinity;
  for (const s of sessions) {
    const ts = Date.parse(s.scheduled_at);
    if (Number.isNaN(ts)) continue;
    if (ts >= now) continue;
    if (ts > bestTs) {
      best = s;
      bestTs = ts;
    }
  }
  return best;
}

/**
 * The 4 onboarding-screen copy blocks, exposed as PURE constants so
 * the verbatim-language test in `tests/lib/onboardingCopy.test.ts`
 * can assert against them. The wizard screen reads these via the
 * `OnboardingCarousel` component.
 *
 * All HIPAA + 1.4.1 + 5.1.2(i) language is verbatim from the PRD.
 */
export const HIPAA_DISCLAIMER =
  'EffectiveSLP is a session-planning tool for licensed clinicians. ' +
  'EffectiveSLP is not a covered entity or business associate under HIPAA. ' +
  'EffectiveSLP does not enter into Business Associate Agreements (BAAs). ' +
  'If you are a clinician in a HIPAA-covered setting, you are responsible ' +
  'for your own BAA with your practice management vendor and your own ' +
  'compliance posture. EffectiveSLP stores data with Supabase, which provides ' +
  'encryption at rest and in transit.';

export const APPLE_GUIDELINE_1_4_1_DISCLAIMER =
  'EffectiveSLP is a session-planning tool for licensed clinicians. ' +
  'It is not a clinical assessment. It is not a diagnostic instrument. ' +
  'It is not a treatment plan.';

export const THIRD_PARTY_AI_CONSENT_TITLE =
  'AI-generated content uses our AI provider';

export const THIRD_PARTY_AI_CONSENT_BODY =
  'When you tap Generate on a session plan or carryover activity, ' +
  'EffectiveSLP sends a small prompt to our AI provider to draft the plan. ' +
  'The prompt includes the goal text, the child’s age in years, ' +
  'your selected materials, and any optional notes you have added. ' +
  'Our AI provider processes the prompt according to its own privacy policy and ' +
  'retains API inputs for up to 30 days for abuse monitoring before ' +
  'automatically deleting them. Read our AI provider’s privacy policy: ' +
  'https://openai.com/policies/privacy-policy/. ' +
  'You can turn AI features off below. EffectiveSLP will still work for ' +
  'manual planning, goals, and parent hand-offs.';
